// Topo da página do empreendimento nos sites com `modelo_pagina_imovel` 3 ou 4: o MESMO painel da página
// do imóvel (pages/imoveis/components/pieces/properties/gallery3/GalleryPiecesProperties.tsx) — painel
// cinza com selo, título, endereço, preço e botões à esquerda; carrossel de fotos com balões à direita.
// Não reaproveita o gallery3: ele conta visualização, aplica `bloquear_fotos`, dispara eventos com o id
// e renderiza o h1 duas vezes. No celular a ordem é foto → painel, só por CSS (h1 continua único).
import Image from 'next/image';
import type { CSSProperties } from 'react';
import { FaFilm, FaMapMarkerAlt, FaPhotoVideo } from 'react-icons/fa';
import { HiMapPin } from 'react-icons/hi2';
import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import CompartilharEmpreendimento from './CompartilharEmpreendimento';
import styles from './EmpreendimentoDetalhe.module.css';
import PrecoMoeda from '../idiomas/PrecoMoeda';

export type FotoTopoEmpreendimento = {
    id: number;
    url_foto: string;
    descricao?: string | null;
};

const TopoPainelEmpreendimento = ({
    titulo,
    nome,
    situacao,
    localizacao,
    precoInicial,
    faixaPreco,
    precosConversor,
    faixaArea,
    logo,
    fotos,
    capa,
    temMapa,
    temVideo,
    whatsappContatoLink,
    linkAgendamento,
    agendamentoEmNovaAba,
    onAbrirFoto,
    onAbrirMapa,
    onAbrirLead,
}: {
    titulo: string;
    nome: string;
    situacao: string;
    localizacao: string;
    precoInicial: string;
    faixaPreco: string;
    // cs2504 (05/10): só nos tenants com conversor de moeda (utils/moedaSite) — os valores crus
    // para converter; sem a prop, os textos prontos de sempre.
    precosConversor?: { min: number; max: number };
    faixaArea: string;
    logo?: string | null;
    fotos: FotoTopoEmpreendimento[];
    capa: string;
    temMapa: boolean;
    temVideo: boolean;
    whatsappContatoLink: string;
    linkAgendamento: string;
    agendamentoEmNovaAba: boolean;
    onAbrirFoto: (index: number) => void;
    onAbrirMapa: () => void;
    onAbrirLead: () => void;
}) => {
    const slides = fotos.length > 0 ? fotos : [{ id: 0, url_foto: capa, descricao: null }];
    // Faixa de preço só acrescenta com mínimo E máximo diferentes ("R$ X a R$ Y"); o resto já está no preço.
    const mostrarFaixaPreco = / a /.test(faixaPreco);
    // gallery3 GalleryPiecesProperties.tsx:98-105 — setas na cor2 do site, fundo cor5.
    const estiloSwiper = {
        '--swiper-navigation-color': 'var(--emp-swiper-nav)',
        background: 'var(--bg)',
        width: '100%',
    } as CSSProperties;

    return (
        <section id="empreendimento" className={styles.topoPainel}>
            <div className={styles.painel}>
                <div className={styles.painelTopo}>
                    {logo ? (
                        <div className={styles.painelLogo}>
                            <Image src={logo} alt={`Logo ${nome}`} fill sizes="180px" className={styles.painelLogoImg} />
                        </div>
                    ) : null}
                    <span className={styles.seloSituacao}>{situacao}</span>
                    {/* H1 ÚNICO da página: nome + cidade/UF é a consulta que se quer ranquear. */}
                    <h1 className={styles.painelTitulo} data-gfm data-fs2>
                        {titulo}
                    </h1>
                    {localizacao ? (
                        <p className={styles.painelEndereco}>
                            <HiMapPin size={18} />
                            <span>{localizacao}</span>
                        </p>
                    ) : null}
                </div>

                <div>
                    <div className={styles.painelPreco} data-gfm1>
                        {precoInicial
                            ? precosConversor
                                ? <>{'A partir de: '}<PrecoMoeda valor={precosConversor.min || precosConversor.max} simbolo="R$" original={precoInicial} /></>
                                : `A partir de: ${precoInicial}`
                            : 'Consulte valores'}
                    </div>
                    {mostrarFaixaPreco || faixaArea ? (
                        <p className={styles.painelInfo}>
                            {mostrarFaixaPreco ? (
                                <span>Faixa de preço: {precosConversor ? (
                                    <>
                                        <PrecoMoeda valor={precosConversor.min} simbolo="R$" original={faixaPreco.split(' a ')[0]} />
                                        {' a '}
                                        <PrecoMoeda valor={precosConversor.max} simbolo="R$" original={faixaPreco.split(' a ')[1]} />
                                    </>
                                ) : faixaPreco}</span>
                            ) : null}
                            {faixaArea ? <span>Área privativa: {faixaArea}</span> : null}
                        </p>
                    ) : null}
                    <div className={styles.painelBotoes}>
                        {linkAgendamento ? (
                            <a
                                href={linkAgendamento}
                                target={agendamentoEmNovaAba ? '_blank' : undefined}
                                rel={agendamentoEmNovaAba ? 'noreferrer' : undefined}
                                className={styles.botaoVisita}
                            >
                                Agendar Visita
                            </a>
                        ) : null}
                        {whatsappContatoLink ? (
                            <a href={whatsappContatoLink} target="_blank" rel="noreferrer" className={styles.botaoContorno}>
                                Fale Conosco
                            </a>
                        ) : null}
                        {!linkAgendamento && !whatsappContatoLink ? (
                            <button type="button" className={styles.botaoVisita} onClick={onAbrirLead}>
                                Quero mais informações
                            </button>
                        ) : null}
                    </div>
                </div>
            </div>

            <div className={styles.painelFotos}>
                <CompartilharEmpreendimento titulo={titulo} nome={nome} className={styles.baloesTopo} />
                <ul className={styles.baloes}>
                    {fotos.length > 0 ? (
                        <li>
                            <button type="button" className={styles.balao} onClick={() => onAbrirFoto(0)}>
                                <FaPhotoVideo className={styles.balaoIcone} />
                                {fotos.length} {fotos.length === 1 ? 'Foto' : 'Fotos'}
                            </button>
                        </li>
                    ) : null}
                    {temMapa ? (
                        <li>
                            <button type="button" className={styles.balao} onClick={onAbrirMapa}>
                                <FaMapMarkerAlt className={styles.balaoIcone} />
                                Mapa
                            </button>
                        </li>
                    ) : null}
                    {temVideo ? (
                        <li>
                            <a href="#video" className={styles.balao}>
                                <FaFilm className={styles.balaoIcone} />
                                Vídeo
                            </a>
                        </li>
                    ) : null}
                </ul>
                <Swiper modules={[Navigation]} slidesPerView="auto" slidesPerGroup={1} spaceBetween={10} navigation style={estiloSwiper}>
                    {slides.map((foto, index) => (
                        <SwiperSlide key={`${foto.id}-${index}`} style={{ maxWidth: '100%', width: '650px' }}>
                            <button
                                type="button"
                                className={styles.slide}
                                onClick={() => onAbrirFoto(index)}
                                aria-label={`Abrir foto ${index + 1} de ${nome}`}
                            >
                                <Image
                                    src={foto.url_foto}
                                    alt={foto.descricao || `Foto ${index + 1} de ${nome}`}
                                    fill
                                    sizes="(max-width: 768px) 100vw, 650px"
                                    priority={index < 2}
                                    className={styles.slideImg}
                                />
                            </button>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
        </section>
    );
};

export default TopoPainelEmpreendimento;
